import { useMemo } from 'react';
import { useCalendarsQuery, Calendar } from './use-calendars-query';
import { useGetCalendarAvailabilitySlotsQuery } from './use-get-calendar-availability-slots-query';
import { useGetAllOpenDaysQuery, OpenDayEventResponse } from './use-get-all-open-days-query';
import { useSelector } from '@bridebook/web/lib/utils';
import { getCalendarIdFromUrl } from '../state';
import { ShowroundCalendarSlot } from '../types/types';

interface UseShowroundCalendarOptions {
  enabled?: boolean;
}

export interface UseShowroundCalendarReturn {
  showroundCalendar: Calendar | undefined;
  openDayCalendar: Calendar | undefined;
  slots: ShowroundCalendarSlot[];
  availabilitiesCount: Record<string, number>;
  openDays: OpenDayEventResponse[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasAvailability: boolean;
}

/**
 * Custom hook that fetches the showround calendar and availability slots for a supplier.
 * Returns showround calendar and availability slots which are used in showrounds manager to compute
 * the availability of the supplier.
 *
 * Open days are fetched via a single server-aggregated request (1 call for all Open Day calendars).
 */
export const useShowroundCalendar = (
  supplierId: string,
  options: UseShowroundCalendarOptions = {},
): UseShowroundCalendarReturn => {
  const calendarIdFromUrl = useSelector(getCalendarIdFromUrl);

  const {
    calendars,
    showroundCalendar: defaultCalendar,
    openDayCalendar,
    isLoading: calendarLoading,
    error: calendarError,
  } = useCalendarsQuery(supplierId, {
    enabled: !!supplierId && options.enabled !== false,
  });

  const showroundCalendar = useMemo(() => {
    if (calendarIdFromUrl) {
      return calendars.find((cal) => cal.id === calendarIdFromUrl) || defaultCalendar;
    }
    return defaultCalendar;
  }, [calendarIdFromUrl, calendars, defaultCalendar]);

  const {
    slots,
    availabilitiesCount,
    isLoading: slotsLoading,
    error: slotsError,
  } = useGetCalendarAvailabilitySlotsQuery(supplierId, showroundCalendar?.id, {
    enabled: !!supplierId && !!showroundCalendar?.id && options.enabled !== false,
  });

  const {
    openDays,
    isLoading: openDaysLoading,
    error: openDaysError,
  } = useGetAllOpenDaysQuery(supplierId, openDayCalendar?.id, {
    enabled: !!supplierId && !!openDayCalendar?.id && options.enabled !== false,
  });

  const isLoading = calendarLoading || slotsLoading || openDaysLoading;
  const isError = !!calendarError || !!slotsError || !!openDaysError;
  const error = calendarError || slotsError || openDaysError;
  const hasAvailability = useMemo(() => Boolean(slots && slots.length > 0), [slots]);

  return {
    showroundCalendar,
    openDayCalendar,
    slots: slots || [],
    availabilitiesCount: availabilitiesCount || {},
    openDays,
    isLoading,
    isError,
    error,
    hasAvailability,
  };
};
